import { useRef, useState, forwardRef, cloneElement } from 'react'
import { useId } from '@react-aria/utils'
import styles from './index.module.css'

const Input = ({
  label,
  labelProps,
  className,
  hint,
  onFocus,
  onBlur,
  value,
  onClick,
  error,
  isValid,
  forwardedRef,
  noBorder, noShadow,
  inputClassName = 'text-primary',
  suffix,
  type,
  isGroup,
  isLast,
  isFirst,
  isTextarea,
  'aria-invalid': ariaInvalid,
  'aria-describedby': ariaDescribedby,
  ...props
}) => {
  const id = useId(props.id)
  const errorId = useId(`error-${id}`)
  const inputRef = useRef()
  const [isFocused, setIsFocused] = useState(false)

  return (
    <>
      <div
        ref={forwardedRef}
        className={`ul-relative ul-flex ul-flex-col ul-w-full ${
          isFocused
            ? 'ul-ring-2 ul-ring-primary ul-z-10'
            : error
            ? 'ul-ring-2 ul-ring-danger'
            : noBorder || props.disabled
            ? ''
            : 'ul-ring-1 ul-ring-gray-light'
        } ${noBorder && !noShadow ? 'ul-shadow' : ''} ${className || ''} ${
          isGroup
            ? isFirst
              ? 'ul-rounded-l-5 ul-rounded-r-none'
              : isLast
              ? 'ul-rounded-r-5 ul-rounded-l-none'
              : ''
            : 'ul-rounded-5'
        } ${isTextarea ? 'ul-p-20 ul-h-120' : 'ul-justify-center ul-px-20 ul-h-60'} ${
          props.disabled ? 'ul-bg-dark-10' : 'ul-bg-white hover:ul-cursor-text'
        }`}
        onClick={() => {
          if (inputRef.current) {
            inputRef.current.focus()
          }

          if (onClick) {
            onClick()
          }
        }}
      >
        <label
          {...labelProps}
          htmlFor={id}
          className={`${
            !value && !isFocused ? 'ul-text-base' : 'ul-text-12'
          } ul-text-dark-60 ul-leading-none ul-pointer-events-none`}
        >
          {label}
        </label>
        {isTextarea ? (
          <>
            <textarea
              {...props}
              ref={inputRef}
              id={id}
              name={id}
              value={value}
              className={`ul-w-full ul-bg-transparent ul-font-medium focus:ul-outline-none ul-flex-grow ul-resize-none ${
                !value && !isFocused ? 'ul-h-0 ul-opacity-0' : ''
              } ${inputClassName}`}
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={error ? errorId : undefined}
              onFocus={(e) => {
                setIsFocused(true)

                if (onFocus) {
                  onFocus(e)
                }
              }}
              onBlur={(e) => {
                setIsFocused(false)

                if (onBlur) {
                  onBlur(e)
                }
              }}
            ></textarea>
            {props.maxLength && (
              <p className="ul-absolute ul-bottom-10 ul-right-10 ul-text-12 ul-text-dark-40">
                {value.length} / {props.maxLength}
              </p>
            )}
          </>
        ) : (
          <input
            {...props}
            type={type}
            ref={inputRef}
            id={id}
            name={id}
            value={value}
            className={`ul-w-full ul-bg-transparent ul-font-medium focus:ul-outline-none ul-truncate ${
              !value && !isFocused ? 'ul-h-0 ul-opacity-0' : ''
            } ${type === 'number' ? styles.typeNumber : ''} ${inputClassName}`}
            aria-invalid={ariaInvalid || (error ? 'true' : undefined)}
            aria-describedby={ariaDescribedby || (error ? errorId : undefined)}
            onFocus={(e) => {
              setIsFocused(true)

              if (onFocus) {
                onFocus(e)
              }
            }}
            onBlur={(e) => {
              setIsFocused(false)

              if (onBlur) {
                onBlur(e)
              }
            }}
          />
        )}
        {suffix
          ? cloneElement(suffix, {
              className: `ul-leading-none ul-absolute ul-top-1/2 ul-transform -ul-translate-y-1/2 ${
                isValid ? 'ul-right-45' : 'ul-right-20'
              }`,
            })
          : null}
      </div>
      {hint ? <p className="ul-text-12 ul-text-dark-40 ul-mt-10">{hint}</p> : null}
      {error ? (
        <p className="ul-text-danger ul-text-14 ul-mt-5" id={errorId}>
          {error}
        </p>
      ) : null}
    </>
  )
}

export default forwardRef(function WrappedInput(props, ref) {
  return <Input {...props} forwardedRef={ref} />
})
